import { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getDashboardPathForRole, getImpersonationDisplayName } from '../../lib/auth';

export const DashboardLayout = ({ allowedRoles }) => {
  const { user, loading, isAuthenticated, stopImpersonation, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  const location = useLocation();
  const isImpersonating = Boolean(user?.impersonation?.active);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        // Optional: you might want it expanded on desktop by default
        // setIsCollapsed(false); 
      } else {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 768) {
      setIsCollapsed(true);
    }
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on backend role (super_admin, wl_admin, employee, agent)
    const redirectPath = getDashboardPathForRole(user?.role);
    return <Navigate to={redirectPath} replace />;
  }

  const handleExitImpersonation = async () => {
    setExitingImpersonation(true);
    try {
      const result = await stopImpersonation();
      if (!result?.success || !result?.user) {
        throw new Error(result?.error || 'Unable to restore your original session');
      }
      window.location.assign(getDashboardPathForRole(result.user.role));
    } catch (err) {
      logout();
      toast.error(err?.message || 'Your session could not be restored. Please sign in again.');
      window.location.assign('/login');
    } finally {
      setExitingImpersonation(false);
    }
  };

  return (
    <div className="min-h-screen bg-background transition-theme">
      {isImpersonating ? (
        <div className="fixed top-0 left-0 right-0 z-[60] border-b border-amber-700/30 bg-amber-500 text-amber-950">
          <div className="flex min-h-12 flex-col gap-2 px-4 py-2 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col">
              <span className="text-xs font-bold tracking-[0.2em]">ADMIN IMPERSONATION</span>
              <span className="text-sm font-medium">
                Viewing as {getImpersonationDisplayName(user)}
              </span>
            </div>
            <Button
              type="button"
              variant="secondary"
              className="h-8 bg-amber-950 text-amber-50 hover:bg-amber-900"
              onClick={handleExitImpersonation}
              disabled={exitingImpersonation}
            >
              {exitingImpersonation ? 'Restoring...' : 'Exit Session'}
            </Button>
          </div>
        </div>
      ) : null}
      <Navbar
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(!isCollapsed)}
        hasTopBanner={isImpersonating}
      />
      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(!isCollapsed)}
        hasTopBanner={isImpersonating}
      />

      {/* Backdrop for mobile */}
      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300"
          onClick={() => setIsCollapsed(true)}
        />
      )}
      <main
        className={cn(
          isImpersonating ? 'pt-28' : 'pt-16',
          'min-h-screen transition-all duration-300',
          isCollapsed ? 'md:pl-16' : 'md:pl-64'
        )}
      >
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
